import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { th } from "./th";
import { en } from "./en";
import { ko } from "./ko";

export type Lang = "th" | "en" | "ko";

const dicts: Record<Lang, unknown> = { th, en, ko };

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Raw lookup for arrays/objects (e.g. notes list, FAQ items). */
  raw: <T = unknown>(key: string) => T;
}

const Ctx = createContext<I18nCtx | null>(null);

function lookup(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((o, k) => (o == null ? undefined : (o as Record<string, unknown>)[k]), obj);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(
    () => (localStorage.getItem("lang") as Lang) || "th",
  );

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    localStorage.setItem("lang", l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let s = lookup(dicts[lang], key);
      if (s == null) s = lookup(dicts.th, key);
      if (typeof s !== "string") return key;
      if (vars) for (const [k, v] of Object.entries(vars)) s = (s as string).replaceAll(`{${k}}`, String(v));
      return s as string;
    },
    [lang],
  );

  const raw = useCallback(
    <T,>(key: string) => (lookup(dicts[lang], key) ?? lookup(dicts.th, key)) as T,
    [lang],
  );

  return <Ctx.Provider value={{ lang, setLang, t, raw }}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useI18n must be used within I18nProvider");
  return c;
}
